import React, { useState, useEffect, useMemo, useCallback } from 'react';

// --- Shadcn/UI & Lucide Imports ---
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SyncDataTypesDisplay } from '@/components/ui/SyncDataTypesDisplay';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
    Loader2,
    Settings,
    AlertCircle,
    BookOpen,
    Clock,
    RefreshCw,
    Search,
} from 'lucide-react';

// --- API Types ---
import type { Account } from '@/lib/api';
import { api } from '@/lib/api';

// --- Hooks ---
import { useSingleEntitySyncStatus } from '@/hooks/useSyncStatusPolling';

// --- Type Definitions ---

export type EntitySettingsData = {
    entityId: string;
    entityName: string;
    prepaymentAssetAccountCodes: string[];
    defaultExpenseAccountCode?: string | null;
    defaultAmortizationMonths?: number;
    amortizationMaterialityThreshold?: number;
    amortizationApply50PercentRule?: boolean;
    autoSyncEnabled?: boolean;
    syncFrequency?: 'hourly' | 'daily' | 'weekly' | 'manual';
    syncSpendMoney?: boolean;
    excludedPnlAccountCodes?: string[];
    // Transaction sync settings
    transactionSyncStartDate?: string;
    // Individual sync data type toggles - DEPRECATED: System now automatically syncs all data types
    /** @deprecated No longer user-configurable - system automatically syncs all data types */
    syncInvoices?: boolean;
    /** @deprecated No longer user-configurable - system automatically syncs all data types */
    syncBills?: boolean;
    /** @deprecated No longer user-configurable - system automatically syncs all data types */
    syncPayments?: boolean;
    /** @deprecated No longer user-configurable - system automatically syncs all data types */
    syncBankTransactions?: boolean;
    /** @deprecated No longer user-configurable - system automatically syncs all data types */
    syncJournalEntries?: boolean;
    // Additional settings
    autoPostProposedJournals?: boolean;
    aiScanningEnabled?: boolean; // New – aligns with wizard's enable_ai_scanning flag
    universalOcrEnabled?: boolean; // Phase 1: OCR all attachments (not just prepayment-flagged)
    emailForwarding?: {
        enabled: boolean;
        maxForwardsPerHour: number;
    };
    baseCurrencyCode?: string;
    initialSyncCompleted?: boolean;
    lastFullSyncDate?: string;
    // Compliance thresholds configuration (camelCase for frontend)
    complianceThresholds?: {
        agedPayablesDays?: number;
        agedReceivablesDays?: number;
        serialCheckerMaxGap?: number;
        serialCheckerInversionLimit?: number;
    };
};

// --- Component Props ---
interface EntitySettingsProps {
    entityId: string;
    // Function to fetch current settings for the entity
    fetchSettings: (entityId: string) => Promise<EntitySettingsData>;
    // Function to fetch the chart of accounts for the entity
    fetchChartOfAccounts: (entityId: string) => Promise<Account[]>;
    // Function to save updated settings
    saveSettings: (entityId: string, settings: Omit<EntitySettingsData, 'entityId' | 'entityName'>) => Promise<void>;
    onClose?: () => void; // Optional close handler if used in a modal
    hideFooter?: boolean; // New prop to hide the footer when used in dialog
    onSaveAction?: (saveHandler: () => Promise<void>, canSave: boolean, isSaving: boolean) => void; // Expose save handler
}

// --- Entity Settings Component ---
export function EntitySettingsManagement({
    entityId,
    fetchSettings,
    fetchChartOfAccounts,
    saveSettings,
    onClose,
    hideFooter = false,
    onSaveAction
}: EntitySettingsProps) {

    const [settings, setSettings] = useState<EntitySettingsData | null>(null);
    const [chartOfAccounts, setChartOfAccounts] = useState<Account[]>([]);
    const [prepaymentCandidates, setPrepaymentCandidates] = useState<string[]>([]);
    const [selectedPrepaymentCodes, setSelectedPrepaymentCodes] = useState<Set<string>>(new Set());
    const [prepaymentSearchTerm, setPrepaymentSearchTerm] = useState<string>('');
    const [defaultAmortizationMonths, setDefaultAmortizationMonths] = useState<number>(12);
    const [amortizationMaterialityThreshold, setAmortizationMaterialityThreshold] = useState<number>(1000);
    const [amortizationApply50PercentRule, setAmortizationApply50PercentRule] = useState<boolean>(true);
    const [autoSyncEnabled, setAutoSyncEnabled] = useState<boolean>(true);
    const [syncFrequency, setSyncFrequency] = useState<'hourly' | 'daily' | 'weekly' | 'manual'>('daily');
    const [syncSpendMoney, setSyncSpendMoney] = useState<boolean>(true);
    const [selectedExcludedPnlCodes, setSelectedExcludedPnlCodes] = useState<Set<string>>(new Set());

    // AI Scanning toggle (wizard parity)
    const [aiScanningEnabled, setAiScanningEnabled] = useState<boolean>(false);
    
    // Universal OCR toggle (Phase 1: OCR all attachments)
    const [universalOcrEnabled, setUniversalOcrEnabled] = useState<boolean>(true);

    // Email forwarding (hard settings)
    const [emailForwardingEnabled, setEmailForwardingEnabled] = useState<boolean>(false);
    const [emailForwardMaxPerHour, setEmailForwardMaxPerHour] = useState<number>(50);

    // Transaction sync settings
    const [transactionSyncStartDate, setTransactionSyncStartDate] = useState<string>('');

    // Sync data types are now automatically configured - no user selection needed

    // Additional settings
    const [autoPostProposedJournals, setAutoPostProposedJournals] = useState<boolean>(false);
    const [baseCurrencyCode, setBaseCurrencyCode] = useState<string>('GBP');

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Use sync status polling hook
    const { syncStatus } = useSingleEntitySyncStatus(entityId);

    // Fetch initial data
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const [fetchedSettings, fetchedAccounts] = await Promise.all([
                    fetchSettings(entityId),
                    fetchChartOfAccounts(entityId)
                ]);
                setSettings(fetchedSettings);
                setChartOfAccounts(fetchedAccounts);
                // Show all accounts for selection - user picks what they want
                const allAccountCodes = fetchedAccounts.map(acc => acc.code);
                setPrepaymentCandidates(allAccountCodes);
                // Show the user's previously selected prepayment accounts as checked
                setSelectedPrepaymentCodes(new Set(fetchedSettings.prepaymentAssetAccountCodes || []));
                setDefaultAmortizationMonths(fetchedSettings.defaultAmortizationMonths || 12);
                setAmortizationMaterialityThreshold(fetchedSettings.amortizationMaterialityThreshold || 1000);
                setAmortizationApply50PercentRule(fetchedSettings.amortizationApply50PercentRule ?? true);
                setAutoSyncEnabled(fetchedSettings.autoSyncEnabled ?? true);
                setSyncFrequency(fetchedSettings.syncFrequency || 'daily');
                setSyncSpendMoney(fetchedSettings.syncSpendMoney ?? true);
                setSelectedExcludedPnlCodes(new Set(fetchedSettings.excludedPnlAccountCodes || []));
                setTransactionSyncStartDate(fetchedSettings.transactionSyncStartDate || '');
                // Sync data types are automatically configured - no need to load individual settings
                setAutoPostProposedJournals(fetchedSettings.autoPostProposedJournals ?? false);
                setBaseCurrencyCode(fetchedSettings.baseCurrencyCode || 'GBP');
                setAiScanningEnabled(fetchedSettings.aiScanningEnabled ?? false);
                setUniversalOcrEnabled(fetchedSettings.universalOcrEnabled ?? true);
                setEmailForwardingEnabled(fetchedSettings.emailForwarding?.enabled ?? false);
                setEmailForwardMaxPerHour(fetchedSettings.emailForwarding?.maxForwardsPerHour ?? 50);
            } catch (err: any) {
                console.error("Error loading entity settings:", err);
                setError(err.message || "Failed to load settings and accounts.");
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [entityId, fetchSettings, fetchChartOfAccounts]);

    // Filter accounts for dropdowns - use backend-classified prepayment candidates
    const assetAccounts = useMemo(() => {
        return chartOfAccounts
            .filter(acc => acc?.code && prepaymentCandidates.includes(acc.code))
            .sort((a, b) => {
                const aSelected = selectedPrepaymentCodes.has(a.code);
                const bSelected = selectedPrepaymentCodes.has(b.code);
                
                // Show checked accounts on top
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                
                // Then sort by code
                return (a?.code || '').localeCompare(b?.code || '');
            });
    }, [chartOfAccounts, prepaymentCandidates, selectedPrepaymentCodes]);

    // Filter asset accounts by search term, keeping checked accounts always visible
    const filteredAssetAccounts = useMemo(() => {
        if (!prepaymentSearchTerm.trim()) return assetAccounts;
        const term = prepaymentSearchTerm.toLowerCase().trim();
        return assetAccounts.filter(acc =>
            selectedPrepaymentCodes.has(acc.code) ||
            acc.code.toLowerCase().includes(term) ||
            acc.name.toLowerCase().includes(term)
        );
    }, [assetAccounts, prepaymentSearchTerm, selectedPrepaymentCodes]);

    // Show only expense accounts for exclusion selection
    const excludedOptions = useMemo(() => {
        return chartOfAccounts
            .filter(acc => acc?.class === 'EXPENSE' && acc?.code)
            .sort((a, b) => {
                const aSelected = selectedExcludedPnlCodes.has(a.code);
                const bSelected = selectedExcludedPnlCodes.has(b.code);
                
                // Show checked accounts on top
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                
                // Then sort by code
                return (a?.code || '').localeCompare(b?.code || '');
            });
    }, [chartOfAccounts, selectedExcludedPnlCodes]);

    // Handle Prepayment Account Selection (Multi-select simulation)
    const handlePrepaymentAccountToggle = (code: string, checked: boolean | 'indeterminate') => {
        setSelectedPrepaymentCodes(prev => {
            const newSet = new Set(prev);
            if (checked === true) {
                newSet.add(code);
            } else {
                newSet.delete(code);
            }
            return newSet;
        });
    };

    // Handle Save - wrap in useCallback to prevent infinite loops
    const handleSave = useCallback(async () => {
        // Check if sync is in progress
        if (syncStatus?.syncStatus?.is_syncing) {
            setError('Cannot modify settings while sync is in progress. Please wait for sync to complete.');
            return;
        }

        setIsSaving(true);
        setError(null);

        // Note: In the "one rule = one email" model, destination email is defined per rule.
        // Entity-level email_forwarding.forward_to_email is optional and treated as a legacy/default field.

        const settingsToSave = {
            prepaymentAssetAccountCodes: Array.from(selectedPrepaymentCodes),
            defaultAmortizationMonths: defaultAmortizationMonths,
            amortizationMaterialityThreshold: amortizationMaterialityThreshold,
            amortizationApply50PercentRule: amortizationApply50PercentRule ?? true,
            autoSyncEnabled: autoSyncEnabled,
            syncFrequency: syncFrequency,
            syncSpendMoney: syncSpendMoney,
            excludedPnlAccountCodes: Array.from(selectedExcludedPnlCodes),
            transactionSyncStartDate: transactionSyncStartDate,
            // Sync data types are now automatically configured
            autoPostProposedJournals: autoPostProposedJournals,
            aiScanningEnabled: aiScanningEnabled,
            universalOcrEnabled: universalOcrEnabled,
            emailForwarding: {
                enabled: emailForwardingEnabled,
                maxForwardsPerHour: emailForwardMaxPerHour
            },
            // baseCurrencyCode removed - should not be editable, comes from accounting system
        };
        try {
            await saveSettings(entityId, settingsToSave);
            // Optionally refetch settings or just update local state if API returns updated data
            setSettings(prev => prev ? { ...prev, ...settingsToSave } : null);
            onClose?.(); // Close dialog immediately after successful save
        } catch (err: any) {
             console.error("Error saving entity settings:", err);
             setError(err.message || "Failed to save settings.");
        } finally {
            setIsSaving(false);
        }
    }, [entityId, selectedPrepaymentCodes, defaultAmortizationMonths, amortizationMaterialityThreshold, amortizationApply50PercentRule, autoSyncEnabled, syncFrequency, syncSpendMoney, selectedExcludedPnlCodes, transactionSyncStartDate, autoPostProposedJournals, aiScanningEnabled, universalOcrEnabled, emailForwardingEnabled, emailForwardMaxPerHour, saveSettings, onClose, syncStatus?.syncStatus?.is_syncing]);

    // Expose save handler to parent - remove handleSave from dependencies to prevent infinite loop
    useEffect(() => {
        if (onSaveAction) {
            onSaveAction(handleSave, selectedPrepaymentCodes.size > 0, isSaving);
        }
    }, [onSaveAction, selectedPrepaymentCodes.size, isSaving, handleSave]);

    if (isLoading) {
        if (hideFooter) {
            return (
                <div className="space-y-6">
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-10 w-full" />
                </div>
            );
        }
        return (
            <Card className="w-full max-w-2xl mx-auto mt-6">
                <CardHeader>
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-4 w-1/3" />
                </CardHeader>
                <CardContent className="space-y-6">
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-10 w-full" />
                </CardContent>
                <CardFooter className="flex justify-end">
                    <Skeleton className="h-9 w-24" />
                </CardFooter>
            </Card>
        );
    }

     if (error && !settings) { // Show error only if loading completely failed
        if (hideFooter) {
            return (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error Loading Settings</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            );
        }
        return (
             <Card className="w-full max-w-2xl mx-auto mt-6">
                 <CardHeader>
                     <CardTitle className="flex items-center text-destructive">
                         <AlertCircle className="h-5 w-5 mr-2"/> Error Loading Settings
                     </CardTitle>
                 </CardHeader>
                 <CardContent>
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                 </CardContent>
             </Card>
        );
    }

     if (!settings) {
         // Should not happen if loading and error states are handled, but as a fallback
         return <p>Could not load entity settings.</p>;
     }

    const contentSection = (
        <div className="space-y-6">
             {/* Prepayment Asset Accounts Selection */}
             <div className="space-y-2">
                <Label className="font-semibold flex items-center">
                    <BookOpen className="h-4 w-4 mr-2"/> Prepayment Asset Accounts
                </Label>
                <p className="text-sm text-muted-foreground">
                    Select the Balance Sheet accounts used for recording prepayments.
                    (Used for v1 identification and journal postings).
                </p>
                <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search accounts..."
                        value={prepaymentSearchTerm}
                        onChange={(e) => setPrepaymentSearchTerm(e.target.value)}
                        className="pl-9 h-9"
                    />
                </div>
                <ScrollArea className="h-40 w-full rounded-md border p-2">
                    <div className="space-y-1">
                         {filteredAssetAccounts.length === 0 && <p className="text-xs text-muted-foreground text-center p-2">{prepaymentSearchTerm ? 'No matching accounts found.' : 'No asset accounts found in Chart of Accounts.'}</p>}
                         {filteredAssetAccounts.map((account) => (
                            <div key={account.code} className="flex items-center space-x-2 p-1 rounded hover:bg-muted">
                                <Checkbox
                                    id={`asset-${account.code}`}
                                    checked={selectedPrepaymentCodes.has(account.code)}
                                    onCheckedChange={(checked) => handlePrepaymentAccountToggle(account.code, checked)}
                                />
                                <label
                                    htmlFor={`asset-${account.code}`}
                                    className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-grow"
                                >
                                    {account.code} - {account.name}
                                </label>
                            </div>
                        ))}
                    </div>
                </ScrollArea>
                {selectedPrepaymentCodes.size === 0 && <p className="text-xs text-red-600 pt-1">At least one prepayment account must be selected.</p>}
             </div>

            {/* Universal OCR Toggle */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="space-y-1">
                    <Label htmlFor="universal-ocr" className="font-medium">
                        Universal OCR
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        Automatically extract text from all invoice and bill attachments using OCR (not just prepayments).
                    </p>
                </div>
                <Switch
                    id="universal-ocr"
                    checked={universalOcrEnabled}
                    onCheckedChange={setUniversalOcrEnabled}
                />
            </div>

            {/* AI Scanning Toggle */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="space-y-1">
                    <Label htmlFor="ai-scanning" className="font-medium">
                        AI Scanning
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        Automatically classify and extract data using AI (may consume credits).
                    </p>
                </div>
                <Switch
                    id="ai-scanning"
                    checked={aiScanningEnabled}
                    onCheckedChange={setAiScanningEnabled}
                />
            </div>

            {/* Inbox Rules (Email Forwarding) Settings */}
            <div className="space-y-3 p-3 border rounded-lg">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <Label htmlFor="email-forwarding-enabled" className="font-medium">
                            Inbox Rules
                        </Label>
                        <p className="text-sm text-muted-foreground">
                            Automatically forward matched inbound emails to an external ingestion mailbox (e.g. Dext).
                        </p>
                    </div>
                    <Switch
                        id="email-forwarding-enabled"
                        checked={emailForwardingEnabled}
                        onCheckedChange={setEmailForwardingEnabled}
                    />
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                        <Label htmlFor="email-forwarding-limit" className="text-sm">
                            Rate limit (per hour)
                        </Label>
                        <Input
                            id="email-forwarding-limit"
                            type="number"
                            value={emailForwardMaxPerHour}
                            onChange={(e) => setEmailForwardMaxPerHour(Number(e.target.value || 50))}
                            min={1}
                            max={50}
                            disabled={true}
                        />
                        <p className="text-xs text-muted-foreground">
                            Hard limit: 50/hour (spam prevention).
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm">
                            Destination email
                        </Label>
                        <p className="text-sm text-muted-foreground">
                            Configured per rule (one rule = one email). You’ll set the destination when creating each forwarding rule.
                        </p>
                    </div>
                </div>
            </div>

            {/* Data Synchronization */}
            <div className="space-y-4">
                <Label className="font-semibold flex items-center">
                    <RefreshCw className="h-4 w-4 mr-2"/> Data Synchronization
                </Label>
                <p className="text-sm text-muted-foreground">
                    All data types are automatically synchronized every 24 hours for optimal performance.
                </p>

                {/* Transaction Sync Start Date */}
                <div className="space-y-2">
                    <Label htmlFor="sync-start-date" className="font-medium">
                        Transaction Sync Start Date
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        Only transactions from this date onwards will be synchronized. Leave empty to sync all historical data.
                    </p>
                    <Input
                        id="sync-start-date"
                        type="date"
                        value={transactionSyncStartDate}
                        onChange={(e) => setTransactionSyncStartDate(e.target.value)}
                        className="w-48"
                    />
                </div>

                {/* Synchronized Data Types Display */}
                <div>
                    <SyncDataTypesDisplay showTitle={true} size="md" />
                </div>

                {/* Auto Sync Settings */}
                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                    <div className="space-y-1">
                        <Label className="font-medium">
                            Auto Sync Schedule
                        </Label>
                        <p className="text-sm text-muted-foreground">
                            Automatic synchronization runs daily at optimized times
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <Badge variant="secondary">Daily</Badge>
                            <p className="text-xs text-muted-foreground mt-1">System managed</p>
                        </div>
                        <Switch
                            checked={autoSyncEnabled}
                            onCheckedChange={setAutoSyncEnabled}
                        />
                    </div>
                </div>
            </div>

            {/* Processing Settings */}
            <div className="space-y-4">
                <Label className="font-semibold flex items-center">
                    <Settings className="h-4 w-4 mr-2"/> Processing Settings
                </Label>

                {/* Default Amortization Period */}
                <div className="space-y-2">
                    <Label htmlFor="amortization-months" className="font-medium flex items-center">
                        <Clock className="h-4 w-4 mr-2"/> Default Amortization Period
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        Default number of months to amortize prepayments over when period cannot be determined automatically.
                    </p>
                    <div className="flex items-center space-x-2">
                        <Input
                            id="amortization-months"
                            type="number"
                            min="1"
                            max="60"
                            value={defaultAmortizationMonths}
                            onChange={(e) => setDefaultAmortizationMonths(parseInt(e.target.value) || 12)}
                            className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">months</span>
                    </div>
                </div>

                {/* Amortization Materiality Threshold */}
                <div className="space-y-2">
                    <Label htmlFor="materiality-threshold" className="font-medium flex items-center">
                        Amortization Materiality Threshold
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        Amounts above this threshold use day-based amortization for accuracy. 
                        Amounts below use 12-month equal distribution for simplicity.
                    </p>
                    <div className="flex items-center space-x-2">
                        <Input
                            id="materiality-threshold"
                            type="number"
                            min="0"
                            step="0.01"
                            value={amortizationMaterialityThreshold}
                            onChange={(e) => setAmortizationMaterialityThreshold(parseFloat(e.target.value) || 1000)}
                            className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">{baseCurrencyCode}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Default: {baseCurrencyCode} 1,000. Higher values = more transactions use simple 12-month distribution.
                    </p>
                </div>

                {/* 50% Rule Setting */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                        <Label htmlFor="apply-50-percent-rule" className="font-medium">
                            Apply 50% Rule for Mid-Month Start Dates
                        </Label>
                        <p className="text-sm text-muted-foreground">
                            When enabled, day-based amortization adjusts start dates to month boundaries (e.g., April 7th → April 1st). 
                            When disabled, uses exact user-selected dates for precise calculations.
                        </p>
                        <p className="text-xs text-yellow-600">
                            💡 Disable this to avoid year-end posting issues when service periods cross fiscal boundaries.
                        </p>
                    </div>
                    <Switch
                        id="apply-50-percent-rule"
                        checked={amortizationApply50PercentRule}
                        onCheckedChange={setAmortizationApply50PercentRule}
                    />
                </div>

                {/* Excluded P&L Accounts */}
                <div className="space-y-2">
                    <Label className="font-medium flex items-center">
                        Excluded P&L Accounts (Optional)
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        Select P&L accounts to exclude from prepayment analysis and processing.
                    </p>
                    <ScrollArea className="h-32 w-full rounded-md border p-2">
                        <div className="space-y-1">
                            {excludedOptions.length === 0 && <p className="text-xs text-muted-foreground text-center p-2">No expense accounts found in Chart of Accounts.</p>}
                            {excludedOptions.map((account) => (
                                <div key={account.code} className="flex items-center space-x-2 p-1 rounded hover:bg-muted">
                                    <Checkbox
                                        id={`exclude-${account.code}`}
                                        checked={selectedExcludedPnlCodes.has(account.code)}
                                        onCheckedChange={(checked) => {
                                            setSelectedExcludedPnlCodes(prev => {
                                                const newSet = new Set(prev);
                                                if (checked === true) {
                                                    newSet.add(account.code);
                                                } else {
                                                    newSet.delete(account.code);
                                                }
                                                return newSet;
                                            });
                                        }}
                                    />
                                    <label
                                        htmlFor={`exclude-${account.code}`}
                                        className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-grow"
                                    >
                                        {account.code} - {account.name}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </ScrollArea>
                </div>

                {/* Base Currency (Read-only) */}
                <div className="space-y-2">
                    <Label className="font-medium">
                        Base Currency
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        The base currency is automatically determined from your accounting system and cannot be changed.
                    </p>
                    <div className="flex items-center space-x-2 p-3 bg-muted rounded-md">
                        <span className="font-medium">{baseCurrencyCode}</span>
                        <span className="text-sm text-muted-foreground">
                            (from {settings?.entityName || 'accounting system'})
                        </span>
                    </div>
                </div>

                {/* Auto Post Proposed Journals */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-1">
                        <Label htmlFor="auto-post-journals" className="font-medium">
                            Auto Post Proposed Journals
                        </Label>
                        <p className="text-sm text-muted-foreground">
                            Automatically post generated journal entries to your accounting system
                        </p>
                    </div>
                    <Switch
                        id="auto-post-journals"
                        checked={autoPostProposedJournals}
                        onCheckedChange={setAutoPostProposedJournals}
                    />
                </div>
            </div>

             {/* Display Save Error */}
             {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Save Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
             )}
        </div>
    );

    return (
        <Card className="w-full max-w-2xl mx-auto mt-6">
            <CardHeader>
                <CardTitle className="flex items-center">
                    <Settings className="h-5 w-5 mr-2" /> Entity Settings: {settings.entityName}
                </CardTitle>
                <CardDescription>
                    Configure prepayment and expense accounts for this entity.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {contentSection}
            </CardContent>
            {!hideFooter && (
                <CardFooter className="flex justify-end space-x-2">
                     {onClose && <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancel</Button>}
                     <Tooltip>
                        <TooltipTrigger asChild>
                            <span>
                                <Button 
                                    onClick={handleSave} 
                                    disabled={isSaving || selectedPrepaymentCodes.size === 0 || syncStatus?.syncStatus?.is_syncing}
                                >
                                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Save Settings
                                </Button>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            {syncStatus?.syncStatus?.is_syncing 
                                ? 'Cannot save settings during sync'
                                : selectedPrepaymentCodes.size === 0
                                    ? 'Select at least one prepayment account'
                                    : 'Save entity settings'
                            }
                        </TooltipContent>
                     </Tooltip>
                </CardFooter>
            )}
        </Card>
    );
}